<template>
  <div class="page-wrap">

    <!-- Reset password overlay -->
    <div class="reset-overlay" v-if="resetPassowordStatus">
      <div class="reset-card">
        <i class="fa fa-spinner fa-spin fa-2x"></i>
        <p>Sending reset link to <strong>{{ email }}</strong></p>
      </div>
    </div>

    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header-left">
          <div class="panel-icon"><i class="fa fa-users"></i></div>
          <div>
            <div class="panel-title">
              Users
              <span class="count-badge" v-if="filteredUsers.length > 0">{{ filteredUsers.length }}</span>
            </div>
            <div class="panel-sub">Manage user accounts and permissions</div>
          </div>
        </div>
        <div class="panel-header-right">
          <router-link :to="{ name: currentUser.u.is_admin == 0 ? 'client.user.create' : 'admin.user.create' }"
            class="btn-add">
            <i class="fa fa-plus"></i> Add User
          </router-link>
        </div>
      </div>

      <!-- Search -->
      <div class="search-section">
        <div class="search-box">
          <i class="fa fa-search"></i>
          <input type="text" v-model="searchInput" placeholder="Search by name…" class="search-input"
            @input="currentPage = 1" />
          <button v-if="searchInput" class="search-clear" @click="searchInput = ''; currentPage = 1">
            <i class="fa fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div class="state-box" v-if="loadingData">
        <i class="fa fa-spinner fa-spin"></i> Loading…
      </div>

      <!-- Empty -->
      <div class="state-box" v-else-if="!loadingData && filteredUsers.length === 0">
        <i class="fa fa-users"></i> No users found
      </div>

      <!-- Table -->
      <div class="table-wrap" v-else>
        <table class="user-table">
          <colgroup>
            <col style="width:160px;" />
            <col style="width:210px;" />
            <col style="width:130px;" />
            <col style="width:150px;" />
            <col style="width:90px;" />
            <col style="width:54px;" />
          </colgroup>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Username</th>
              <th>Role</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in paginatedUsers" :key="item.id" v-show="item.id != currentUser.u.id">
              <td class="col-name">{{ item.name }}</td>
              <td class="col-email">{{ item.email }}</td>
              <td>{{ item.username }}</td>

              <!-- Role cell -->
              <td>
                <template v-if="!item.change_status">
                  <span class="role-badge" @click="editRole(index)">
                    {{ item.roles }}
                    <i class="fa fa-pencil-alt"></i>
                  </span>
                </template>
                <div class="role-edit" v-else>
                  <select class="role-select" v-model="item.new_role">
                    <option value="admin">Admin</option>
                    <option value="staff">Staff</option>
                  </select>
                  <button v-if="item.roles != item.new_role" class="role-btn confirm"
                    @click="updateUserStatus(index, item.id, item.is_active, item.username, item.new_role, 'role')"><i
                      class="fa fa-check"></i></button>
                  <button class="role-btn cancel" @click="closeChangeStatusFlag(index)">
                    <i class="fa fa-times"></i>
                  </button>
                </div>
              </td>

              <!-- Status toggle -->
              <td>
                <label class="toggle">
                  <input type="checkbox" v-model="item.is_active"
                    @change="updateUserStatus(index, item.id, item.is_active, item.username, item.new_role, 'status')" />
                  <span class="toggle-slider"></span>
                </label>
              </td>

              <!-- 3-dot actions -->
              <td style="text-align:right; overflow:visible;" @click.stop>
                <div class="action-wrap" :class="{ open: openIndex === index }"
                  @click.stop="toggleDropdown(index, $event)">
                  <div class="action-btn">•••</div>
                  <div class="action-menu" :style="openIndex === index ? menuStyle : null">
                    <div class="menu-item orange" @click.stop="openResetModal(item)">
                      <i class="fa fa-sync"></i> Reset Password
                    </div>
                    <div class="menu-item success" @click.stop="openSetPasswordModal(item)">
                      <i class="fa fa-key"></i> Set Password
                    </div>
                    <div class="menu-item divider"></div>
                    <div class="menu-item danger" @click.stop="openDeleteModal(item)">
                      <i class="fa fa-trash"></i> Delete
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div class="pagination-bar" v-if="totalPages > 1">
          <div class="page-info">
            Showing {{ pageStart }}–{{ pageEnd }} of {{ filteredUsers.length }} users
          </div>
          <div class="page-controls">
            <button class="page-btn" :disabled="currentPage === 1" @click="currentPage = 1">
              <i class="fa fa-angle-double-left"></i>
            </button>
            <button class="page-btn" :disabled="currentPage === 1" @click="currentPage--">
              <i class="fa fa-angle-left"></i>
            </button>
            <button v-for="p in visiblePages" :key="p" class="page-btn" :class="{ active: p === currentPage }"
              @click="currentPage = p">{{ p }}</button>
            <button class="page-btn" :disabled="currentPage === totalPages" @click="currentPage++">
              <i class="fa fa-angle-right"></i>
            </button>
            <button class="page-btn" :disabled="currentPage === totalPages" @click="currentPage = totalPages">
              <i class="fa fa-angle-double-right"></i>
            </button>
          </div>
          <div class="page-size">
            <select v-model.number="pageSize" @change="currentPage = 1" class="size-select">
              <option :value="10">10 / page</option>
              <option :value="20">20 / page</option>
              <option :value="50">50 / page</option>
            </select>
          </div>
        </div>
      </div>

    </div>

    <!-- ── Delete Modal ── -->
    <div class="modal-overlay" v-if="activeModal === 'delete'" @click.self="activeModal = null">
      <div class="confirm-dialog">
        <div class="modal-icon-wrap">
          <div class="modal-icon icon-danger"><i class="fa fa-trash"></i></div>
        </div>
        <div class="modal-body-inner">
          <h3>Delete User</h3>
          <p>Are you sure you want to delete <strong>{{ modalItem && modalItem.name }}</strong>?<br />This action cannot
            be
            undone.</p>
        </div>
        <div class="confirm-footer">
          <button class="btn-cancel" @click="activeModal = null">Cancel</button>
          <button class="btn-confirm-danger" @click="confirmDelete">Delete</button>
        </div>
      </div>
    </div>

    <!-- ── Reset Password Modal ── -->
    <div class="modal-overlay" v-if="activeModal === 'reset'" @click.self="activeModal = null">
      <div class="confirm-dialog">
        <div class="modal-icon-wrap">
          <div class="modal-icon icon-orange"><i class="fa fa-sync"></i></div>
        </div>
        <div class="modal-body-inner">
          <h3>Reset Password</h3>
          <p>A password reset link will be sent to:</p>
          <span class="modal-highlight">{{ modalItem && modalItem.email }}</span>
        </div>
        <div class="confirm-footer">
          <button class="btn-cancel" @click="activeModal = null">Cancel</button>
          <button class="btn-confirm-orange" @click="confirmReset">Send Reset</button>
        </div>
      </div>
    </div>

    <!-- ── Set Password Modal ── -->
    <div class="modal-overlay" v-if="activeModal === 'setPassword'" @click.self="activeModal = null">
      <div class="confirm-dialog">
        <div class="modal-icon-wrap">
          <div class="modal-icon icon-green"><i class="fa fa-key"></i></div>
        </div>
        <div class="modal-body-inner">
          <h3>Set New Password</h3>
          <p>For user: <strong>{{ modalItem && modalItem.username }}</strong></p>
        </div>
        <div class="modal-form">
          <label class="form-label">New Password</label>
          <div class="password-input-wrap">
            <input :type="showPassword ? 'text' : 'password'" v-model="newPassword" placeholder="Enter new password"
              :class="{ 'has-error': passwordError }" />
            <button type="button" class="eye-btn" @click="showPassword = !showPassword">
              <i :class="showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
            </button>
          </div>
          <p v-if="passwordError" class="field-error">{{ passwordError }}</p>
          <div class="rules-list">
            <div v-for="rule in rules" :key="rule.message" class="rule-item"
              :class="{ pass: rule.regex.test(newPassword) }">
              <i :class="rule.regex.test(newPassword) ? 'fa fa-check-circle' : 'fa fa-circle'"></i>
              {{ rule.message }}
            </div>
          </div>
        </div>
        <div class="confirm-footer">
          <button class="btn-cancel" @click="activeModal = null">Cancel</button>
          <button class="btn-confirm-green" @click="confirmSetPassword">Save Password</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import {
  FETCH_B_USER,
  EMAIL_PASSWORD_B_USER,
  RESET_USER_PASSWORD,
  DELETE_B_USER
} from "@/core/services/store/actions.type";
import { mapGetters } from "vuex";
import { UPDATE_B_USER } from "../../../core/services/store/actions.type";

export default {
  data() {
    return {
      rules: [
        { message: "One lowercase letter required.", regex: /[a-z]+/ },
        { message: "One uppercase letter required.", regex: /[A-Z]+/ },
        { message: "8 characters minimum.", regex: /.{8,}/ },
        { message: "One number required.", regex: /[0-9]+/ },
        { message: "One special character required.", regex: /[!@#$%^&*]+/ }
      ],
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "" },
        { id: 2, title: "Setting", route: "" },
        { id: 3, title: "Users", route: "" }
      ],
      loadingData: false,
      searchInput: "",
      resetPassowordStatus: false,
      email: "",
      editrole: false,
      openIndex: null,
      menuStyle: null,
      currentPage: 1,
      pageSize: 10,
      activeModal: null,
      modalItem: null,
      newPassword: "",
      showPassword: false,
      passwordError: ""
    };
  },
  computed: {
    ...mapGetters(["getUsers", "currentUser"]),
    filteredUsers() {
      let users = this.getUsers;
      if (this.searchInput) {
        const q = this.searchInput.toUpperCase();
        users = users.filter(u => u.name.toUpperCase().includes(q));
      }
      return users;
    },
    totalPages() {
      return Math.ceil(this.filteredUsers.length / this.pageSize);
    },
    pageStart() {
      return (this.currentPage - 1) * this.pageSize + 1;
    },
    pageEnd() {
      return Math.min(this.currentPage * this.pageSize, this.filteredUsers.length);
    },
    paginatedUsers() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredUsers.slice(start, start + this.pageSize);
    },
    visiblePages() {
      const total = this.totalPages;
      const cur = this.currentPage;
      const delta = 2;
      const pages = [];
      for (let i = Math.max(1, cur - delta); i <= Math.min(total, cur + delta); i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  created() {
    if (this.$route.path == "/admin/users") {
      this.breadCrumbs[0].route = "admin.dashboard";
    } else {
      this.breadCrumbs[0].route = "client.dashboard";
    }
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.getUserData();
  },
  mounted() {
    document.addEventListener("click", this.closeDropdown);
    window.addEventListener("scroll", this.closeDropdown, true);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closeDropdown);
    window.removeEventListener("scroll", this.closeDropdown, true);
  },
  methods: {
    editRole(i) {
      this.paginatedUsers[i].change_status = true;
      this.editrole = true;
    },
    closeChangeStatusFlag(index) {
      this.paginatedUsers[index].change_status = false;
    },
    toggleDropdown(index, event) {
      if (this.openIndex === index) {
        this.openIndex = null;
        this.menuStyle = null;
        return;
      }
      this.openIndex = index;
      this.$nextTick(() => {
        const rect = event.currentTarget.getBoundingClientRect();
        const menuH = 120;
        const rightOffset = window.innerWidth - rect.right;
        if (window.innerHeight - rect.bottom < menuH + 8) {
          this.menuStyle = { position: 'fixed', bottom: (window.innerHeight - rect.top + 4) + 'px', top: 'auto', right: rightOffset + 'px' };
        } else {
          this.menuStyle = { position: 'fixed', top: (rect.bottom + 4) + 'px', bottom: 'auto', right: rightOffset + 'px' };
        }
      });
    },
    closeDropdown() {
      this.openIndex = null;
      this.menuStyle = null;
    },
    openSetPasswordModal(item) {
      this.modalItem = item;
      this.newPassword = "";
      this.showPassword = false;
      this.passwordError = "";
      this.activeModal = "setPassword";
      this.openIndex = null;
    },
    confirmSetPassword() {
      if (!this.newPassword) { this.passwordError = "Please enter a password."; return; }
      for (let rule of this.rules) {
        if (!rule.regex.test(this.newPassword)) { this.passwordError = rule.message; return; }
      }
      this.passwordError = "";
      this.$store.dispatch(EMAIL_PASSWORD_B_USER, { user_id: this.modalItem.id, password: this.newPassword })
        .then(data => { this.$toastr.s(data.msg); })
        .catch(err => { this.$toastr.e(err.error?.password || "Failed to set password."); });
      this.activeModal = null;
    },
    updateUserStatus(index, id, flag, username, role, status) {
      this.errors = [];
      var _data = {};
      var text = "";
      if (status == "status") {
        _data = { is_active: flag, status_: true };
        text = "Would you like to change the status of user: " + username + "?";
      } else {
        _data = { status_: true, roles: role };
        text = "Would you like to change the role of user: " + username + "?";
      }
      this.$swal
        .fire({
          title: "Are you sure?",
          text,
          confirmButtonText: "Save",
          cancelButtonText: "Cancel",
          showCancelButton: true,
          showCloseButton: true
        })
        .then(result => {
          if (result.isConfirmed) {
            this.$store
              .dispatch(UPDATE_B_USER, { id, data: _data })
              .then(data => {
                this.$toastr.s(data.msg);
                if (status != "status") {
                  this.paginatedUsers[index].change_status = false;
                  this.paginatedUsers[index].roles = role;
                }
                this.getUserData();
              })
              .catch(() => this.getUserData());
          } else {
            this.getUserData();
          }
        });
    },
    openResetModal(item) {
      this.modalItem = item;
      this.activeModal = "reset";
      this.openIndex = null;
    },
    confirmReset() {
      const slug = this.$route.path !== "/admin/users"
        ? this.$route.params.client_slug
        : this.currentUser.c.slug;
      this.email = this.modalItem.email;
      this.resetPassowordStatus = true;
      this.activeModal = null;
      this.$store.dispatch(RESET_USER_PASSWORD, { email: this.modalItem.email, client_slug: slug })
        .then(data => { this.resetPassowordStatus = false; this.$toastr.s(data.msg); })
        .catch(() => { this.resetPassowordStatus = false; });
    },
    openDeleteModal(item) {
      this.modalItem = item;
      this.activeModal = "delete";
      this.openIndex = null;
    },
    confirmDelete() {
      this.$store.dispatch(DELETE_B_USER, this.modalItem.id).then(data => {
        this.$toastr.s(data.msg);
        this.getUserData();
      });
      this.activeModal = null;
    },
    getUserData() {
      const _data = {
        is_client: this.currentUser.u.is_client,
        is_service_provider: this.currentUser.u.is_service_provider
      };
      this.loadingData = true;
      this.$store
        .dispatch(FETCH_B_USER, _data)
        .then(() => { this.loadingData = false; })
        .catch(() => { this.loadingData = false; });
    }
  }
};
</script>

<style lang="scss" scoped>
$font: 'Segoe UI', sans-serif;
$blue: #389ad4;
$dark: #0d1b3e;
$border: #dde2ec;
$green: #276749;

.page-wrap {
  padding: 24px;
  font-family: $font;
}

/* ── Reset overlay ── */
.reset-overlay {
  position: fixed;
  inset: 0;
  background: rgba(13, 27, 62, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(3px);
}

.reset-card {
  background: #fff;
  border-radius: 16px;
  padding: 40px 48px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(13, 27, 62, 0.2);

  i {
    color: $blue;
    margin-bottom: 16px;
  }

  p {
    margin: 16px 0 0;
    color: $dark;
    font-size: 14px;
  }
}

/* ── Panel ── */
.panel-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(13, 27, 62, 0.08);
  overflow: clip;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 18px;
  border-bottom: 1px solid #f0f3fa;
  background: #fafbff;
  flex-wrap: wrap;
  gap: 12px;
}

.panel-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.panel-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, $blue, #2176ae);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.panel-title {
  font-size: 15px;
  font-weight: 700;
  color: $dark;
  display: flex;
  align-items: center;
  gap: 8px;
}

.panel-sub {
  font-size: 12px;
  color: #8a94a6;
  margin-top: 2px;
}

.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: $blue;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 20px;
  padding: 1px 8px;
  min-width: 24px;
}

.panel-header-right {
  display: flex;
  align-items: center;
}

.btn-add {
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  background: $blue;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  border: none;
  transition: background 0.15s;

  &:hover {
    background: #2176ae;
    color: #fff;
  }
}

/* ── Search ── */
.search-section {
  padding: 14px 24px;
  border-bottom: 1px solid #f0f3fa;
  background: #fafbff;
}

.search-box {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 0 10px;
  max-width: 320px;
  gap: 8px;

  &:focus-within {
    border-color: $blue;
  }

  i {
    color: #a0aab8;
    font-size: 13px;
  }
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 13px;
  font-family: $font;
  color: $dark;
  padding: 8px 0;
  background: transparent;
}

.search-clear {
  border: none;
  background: none;
  cursor: pointer;
  color: #a0aab8;
  padding: 0;

  &:hover {
    color: $dark;
  }
}

/* ── States ── */
.state-box {
  text-align: center;
  padding: 56px 24px;
  color: #a0aab8;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  i {
    font-size: 18px;
  }
}

/* ── Table ── */
.table-wrap {
  overflow-x: auto;
}

.user-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  font-family: $font;
  table-layout: fixed;

  thead tr {
    background: #f7f9fc;
    border-bottom: 2px solid #e8ecf5;
  }

  th {
    padding: 10px 14px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #7a8499;
    white-space: nowrap;
    position: sticky;
    top: 0;
    background: #f7f9fc;
    z-index: 1;
  }

  td {
    padding: 11px 14px;
    color: #2d3748;
    border-bottom: 1px solid #f0f3fa;
    vertical-align: middle;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  tbody tr:hover {
    background: #fafbff;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.col-name {
  font-weight: 500;
  color: $dark;
}

.col-email {
  color: #4a5568;
}

.col-mono {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  font-weight: 600;
}

/* ── Role badge + inline edit ── */
.role-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #eef7fb;
  color: $blue;
  border: 1px solid #cde6f7;
  border-radius: 6px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  i {
    font-size: 10px;
    color: #a0aab8;
  }

  &:hover {
    background: #ddf0fb;
  }
}

.role-edit {
  display: flex;
  align-items: center;
  gap: 4px;
}

.role-select {
  height: 28px;
  border-radius: 6px;
  border: 1.5px solid $border;
  font-size: 12px;
  font-family: $font;
  padding: 0 6px;
  color: $dark;
  outline: none;

  &:focus {
    border-color: $blue;
  }
}

.role-btn {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: background 0.15s;

  &.confirm {
    background: #f0faf4;
    color: $green;

    &:hover {
      background: #d0f0de;
    }
  }

  &.cancel {
    background: #fff5f5;
    color: #c62828;

    &:hover {
      background: #ffd9d9;
    }
  }
}

/* ── Status toggle ── */
.toggle {
  position: relative;
  display: inline-block;
  width: 38px;
  height: 20px;
  margin: 0;

  input {
    display: none;
  }
}

.toggle-slider {
  position: absolute;
  inset: 0;
  border-radius: 20px;
  cursor: pointer;
  background: #dde2ec;
  transition: background 0.2s;

  &::before {
    content: '';
    position: absolute;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #fff;
    left: 3px;
    top: 3px;
    transition: transform 0.2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  }
}

.toggle input:checked+.toggle-slider {
  background: $green;

  &::before {
    transform: translateX(18px);
  }
}

/* ── Action dropdown ── */
.action-wrap {
  position: relative;
  display: inline-block;

  .action-btn {
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    background: #f5f7fa;
    border: 1px solid #e2e8f0;
    cursor: pointer;
    font-size: 16px;
    font-weight: 700;
    color: #4a5568;
    letter-spacing: 1px;
    transition: background 0.15s;

    &:hover {
      background: #eef2fb;
      color: #185FA5;
      border-color: #c5d4f0;
    }
  }

  .action-menu {
    display: none;
    position: fixed;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    min-width: 160px;
    box-shadow: 0 8px 24px rgba(13, 27, 62, 0.12);
    z-index: 9999;
    overflow: hidden;

    .menu-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 9px 14px;
      font-size: 13px;
      color: #2d3748;
      cursor: pointer;
      transition: background 0.15s;

      i {
        width: 16px;
        font-size: 13px;
      }

      &:hover {
        background: #f8fbff;
      }

      &.orange {
        color: #c05621;

        i {
          color: #c05621;
        }

        &:hover {
          background: #fff7ed;
          color: #9c4318;

          i {
            color: #9c4318;
          }
        }
      }

      &.success {
        color: #276749;

        i {
          color: #276749;
        }

        &:hover {
          background: #f0fdf4;
          color: #1d5230;

          i {
            color: #1d5230;
          }
        }
      }

      &.danger {
        color: #c62828;

        i {
          color: #c62828;
        }

        &:hover {
          background: #fff5f5;
          color: #9b1c1c;

          i {
            color: #9b1c1c;
          }
        }
      }

      &.divider {
        border-top: 1px solid #f0f3fa;
        pointer-events: none;
        padding: 0;
        margin: 0;
      }
    }
  }

  &.open .action-menu {
    display: block;
  }
}

/* ── Custom modals ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(13, 27, 62, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(3px);
  padding: 20px;
}

.confirm-dialog {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(13, 27, 62, 0.22);
  width: 100%;
  max-width: 420px;
  overflow: hidden;
}

.modal-icon-wrap {
  display: flex;
  justify-content: center;
  padding: 28px 24px 0;
}

.modal-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;

  &.icon-danger {
    background: #fed7d7;
    color: #c62828;
  }

  &.icon-orange {
    background: #feebc8;
    color: #c05621;
  }

  &.icon-green {
    background: #c6f6d5;
    color: #276749;
  }
}

.modal-body-inner {
  padding: 18px 28px 8px;
  text-align: center;

  h3 {
    font-size: 16px;
    font-weight: 700;
    color: $dark;
    margin: 10px 0 8px;
  }

  p {
    font-size: 13px;
    color: #7a8499;
    margin: 0;
    line-height: 1.6;
  }
}

.modal-highlight {
  display: inline-block;
  margin: 10px 0 0;
  background: #f0f4fa;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  color: $dark;
  word-break: break-all;
}

.modal-form {
  padding: 4px 28px 0;
  text-align: left;

  .form-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #7a8499;
    margin-bottom: 6px;
    display: block;
  }

  .password-input-wrap {
    position: relative;

    input {
      width: 100%;
      height: 40px;
      border: 1.5px solid $border;
      border-radius: 8px;
      padding: 0 42px 0 12px;
      font-size: 13px;
      color: $dark;
      outline: none;
      transition: border-color 0.2s;
      box-sizing: border-box;

      &:focus {
        border-color: #276749;
        box-shadow: 0 0 0 3px rgba(39, 103, 73, 0.1);
      }

      &.has-error {
        border-color: #e53e3e;
      }
    }

    .eye-btn {
      position: absolute;
      right: 10px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      cursor: pointer;
      color: #a0aab8;
      font-size: 14px;
      padding: 0;

      &:hover {
        color: #4a5568;
      }
    }
  }

  .field-error {
    color: #e53e3e;
    font-size: 12px;
    margin: 6px 0 0;
  }

  .rules-list {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 5px;

    .rule-item {
      display: flex;
      align-items: center;
      gap: 7px;
      font-size: 12px;
      color: #a0aab8;
      transition: color 0.15s;

      i {
        font-size: 11px;
      }

      &.pass {
        color: #276749;
      }
    }
  }
}

.confirm-footer {
  display: flex;
  gap: 10px;
  padding: 16px 28px 24px;

  button {
    flex: 1;
    height: 40px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: 1.5px solid;
    font-family: $font;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }
}

.btn-cancel {
  background: #fff;
  color: #4a5568;
  border-color: $border;

  &:hover {
    border-color: #a0aab8;
    color: $dark;
  }
}

.btn-confirm-danger {
  background: #c62828;
  color: #fff;
  border-color: #c62828;

  &:hover {
    background: #9b1c1c;
    border-color: #9b1c1c;
  }
}

.btn-confirm-orange {
  background: #c05621;
  color: #fff;
  border-color: #c05621;

  &:hover {
    background: #9c4318;
    border-color: #9c4318;
  }
}

.btn-confirm-green {
  background: #276749;
  color: #fff;
  border-color: #276749;

  &:hover {
    background: #1d5230;
    border-color: #1d5230;
  }
}

/* ── Pagination ── */
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-top: 1px solid #f0f3fa;
  flex-wrap: wrap;
  gap: 10px;
}

.page-info {
  font-size: 12px;
  color: #8a94a6;
}

.page-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.page-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border-radius: 6px;
  border: 1.5px solid $border;
  background: #fff;
  color: #4a5568;
  font-size: 12px;
  font-family: $font;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    border-color: $blue;
    color: $blue;
    background: #eef7fb;
  }

  &.active {
    background: $blue;
    color: #fff;
    border-color: $blue;
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
}

.page-size {
  .size-select {
    height: 32px;
    border-radius: 6px;
    border: 1.5px solid $border;
    font-size: 12px;
    font-family: $font;
    color: #4a5568;
    padding: 0 8px;
    outline: none;

    &:focus {
      border-color: $blue;
    }
  }
}
</style>
